<template>
  <OnBoarding>
    <template>
      <CRow>
        <CCol>
          <FindYourPhotos/>

          <OnBoardingHeader  :title="$pgettext('login.title', 'Workspace for photographers')"
                            :subtitle="pageTitle" />

          <!-- Utente già autenticato SAAS con region diversa -->
          <template v-if="isAuthenticated && isSaasWithDifferentRegion">
            <div class="font-weight-semi-bold mb-3 text-info text-center">
              <translate translate-context="register.logged">You are logged in as:</translate>
              {{ user.email }}
            </div>

            <div class="mb-3 text-center">
              <translate translate-context="register.saas_existing">You already have a self-service account registered. You can go to your dashboard or log out and register with a different account.</translate>
            </div>

            <CButton @click="doLogout" color="primary" block class="mt-2">
              <translate translate-context="register.button">Log out and register</translate>
            </CButton>
          </template>

          <!-- Utente già autenticato (non SAAS o senza region) -->
          <template v-else-if="isAuthenticated">
            <div class="font-weight-semi-bold mb-3 text-info text-center">
              <translate translate-context="register.logged">You are logged in as:</translate>
              {{ user.email }}
            </div>

            <div class="d-flex mb-3">
              <CInputCheckbox custom
                              :checked.sync="v$.termsAgreement.$model"
                              :is-valid="!v$.termsAgreement.$error"
                              id="termsAgreement"
                              :placeholder="$pgettext('auth.placeholder','Terms and conditions')">
              </CInputCheckbox>
              <div>
                <label for="termsAgreement" class="form-label mb-0" v-html="termsAgreementText"/>
                <div class="invalid-feedback d-block" v-for="error of v$.termsAgreement.$errors" :key="error.$uid">
                  <div class="error-msg" v-translate translate-context="auth.terms.error">You must accept the terms and
                    conditions
                  </div>
                </div>
              </div>
            </div>
             <div class="d-flex mb-3">
              <CInputCheckbox custom
                              :checked.sync="v$.readAndAgree.$model"
                              :is-valid="!v$.readAndAgree.$error"
                              id="readAndAgree">
              </CInputCheckbox>
              <div>
                <label for="readAndAgree" class="form-label mb-0" v-html="readTermsText" />
                <div class="invalid-feedback d-block" v-for="error of v$.readAndAgree.$errors" :key="error.$uid">
                  <div class="error-msg" v-translate translate-context="auth.terms.error">You must read and approve the terms and conditions
                  </div>
                </div>
              </div>
            </div>

            <PButton :loading="loading" type="button" color="primary" block @click="confirmAccount">
              <translate translate-context="register.button">Continue with this account</translate>
            </PButton>

            <CButton @click="doLogout" color="secondary" block class="mt-2">
              <translate translate-context="register.button">Change account</translate>
            </CButton>
          </template>

          <!-- Utente non autenticato (flusso originale) -->
          <template v-else>
            <PAlert v-if="isErrorVisible" class="mb-2" color="danger">{{ errorMessage }}</PAlert>

            <CInput
                ref="email"
                :placeholder="$pgettext('auth.placeholder','Email address')"
                v-model="v$.email.$model"
                :is-valid="!v$.email.$error"
                :class="['input-segmented', !v$.email.$error ? 'is-valid' : 'is-invalid']"
                autocomplete="off"
                @keyup.enter="signup">
              <template v-slot:prepend-content>
                <CIcon name="cipEmail" />
              </template>
              <template v-slot:invalid-feedback>
                <div class="invalid-feedback d-block" v-for="error of v$.email.$errors" :key="error.$uid">
                  <div class="error-msg">{{ error.$message }}</div>
                </div>
              </template>
            </CInput>


            <div class="d-flex mb-1">
              <CInputCheckbox custom
                              :checked.sync="v$.termsAgreement.$model"
                              :is-valid="!v$.termsAgreement.$error"
                              id="termsAgreement">
              </CInputCheckbox>
              <div>
                <label for="termsAgreement" class="form-label mb-0" v-html="termsAgreementText"/>
                <div class="invalid-feedback d-block" v-for="error of v$.termsAgreement.$errors" :key="error.$uid">
                  <div class="error-msg" v-translate translate-context="auth.terms.error">You must accept the terms and
                    conditions
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex mb-3">
              <CInputCheckbox custom
                              :checked.sync="v$.readAndAgree.$model"
                              :is-valid="!v$.readAndAgree.$error"
                              id="readAndAgree">
              </CInputCheckbox>
              <div>
                <label for="readAndAgree" class="form-label mb-0" v-html="readTermsText" />
                <div class="invalid-feedback d-block" v-for="error of v$.readAndAgree.$errors" :key="error.$uid">
                  <div class="error-msg" v-translate translate-context="auth.terms.error">You must read and approve the terms and conditions
                  </div>
                </div>
              </div>
            </div>


            <PButton :loading="loading" type="button" color="primary" block v-translate @click="signup">Create account</PButton>

            <div class="text-center info">
              <span v-translate>otherwise</span>
            </div>
            <SocialLogin allow-register/>
            <AlreadyHaveAccount/>
          </template>
        </CCol>
      </CRow>
    </template>
  </OnBoarding>
</template>

<script>
import SocialLogin from "@/domain/login/components/providers/SocialLoginButtons.vue";
import {mapActions, mapGetters} from "vuex";
import {useVuelidate} from "@vuelidate/core";
import {methods, computed} from "@/domain/login/mixin";
import OnBoarding from "@/domain/login/OnBoarding.vue";
import AlreadyHaveAccount from "@/domain/login/components/AlreadyHaveAccount.vue";
import OnBoardingHeader from "@/domain/login/components/OnBoardingHeader.vue";
import {
  required$,
  email$,
  sameAs$
} from "@/domain/core/utils/validators";
import PButton from "@/components/PButton.vue";
import PAlert from "@/components/PAlert.vue";
import FindYourPhotos from "@/domain/login/components/FindYourPhotos.vue";

export default {
  name: 'Register',
  components: {FindYourPhotos, PAlert, PButton, OnBoardingHeader, AlreadyHaveAccount, OnBoarding, SocialLogin},

  data() {
    return {
      loading: false,
      email: null,
      termsAgreement: false,
      readAndAgree: false,
      submitted: false,
      error: null
    }
  },
  async mounted() {
    if (this.invitation)
      this.email = this.invitation.email

    if (!this.isAuthenticated && this.$refs.email) {
      this.$refs.email.$el.children[0].focus()
    }
    await this.checkQueryParams()

    if (this.isAuthenticated) {
      await this.fetchUserStatus()
      if (this.canSkipDisambiguation()) {
        await this.afterLogin()
      }
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    if (this.isAuthenticated) {
      return {
        termsAgreement: {sameAs: sameAs$(true)},
        readAndAgree: {sameAs: sameAs$(true)},
      }
    }
    return {
      email: {required$, email$},
      termsAgreement: {sameAs: sameAs$(true)},
      readAndAgree: {sameAs: sameAs$(true)},
    }
  },
  computed: {
    ...mapGetters('login', ['userLanguage', 'registrationFlow', 'isAuthenticated', 'user']),
    ...mapGetters('userStatus', ['hasRegion', 'isSaasUser']),
    ...computed,
    isSaasWithDifferentRegion() {
      const region = this.$route.query.region
      return this.isSaasUser && region && !this.hasRegion(region)
    },
    pageTitle() {
      if (this.isAuthenticated && this.isSaasWithDifferentRegion) {
        return this.$pgettext('register.title', 'Account already registered')
      }
      if (this.isAuthenticated) {
        return this.$pgettext('register.title', 'Confirm account')
      }
      return this.$gettext('Create account')
    },
    isValid() {
      return !this.v$.$invalid
    },
    isErrorVisible() {
      return !!this.error
    },
    errorMessage() {
      if (this.error) return this.error
      return null
    },
    termsAgreementText() {
      const translatedText = this.$gettext("I declare that I am at least 18 years old and have read and agree to the [TERMS_LINK] and the [PRIVACY_LINK] of GetPica.");

      const termsLink = `<a href="${this.policyUrl.terms}" target="_blank">${this.$gettext('Terms and Conditions')}</a>`;
      const privacyLink = `<a href="${this.policyUrl.privacy}" target="_blank">${this.$gettext('Privacy Policy')}</a>`;

      return translatedText
        .replace('[TERMS_LINK]', termsLink)
        .replace('[PRIVACY_LINK]', privacyLink);
    },
    readTermsText() {
      const translatedText = this.$gettext("I have read and approved the art. 5-7-10-15 of the [TERMS_LINK].");
      const termsLink = `<a href="${this.policyUrl.terms}" target="_blank">${this.$gettext('Terms and Conditions')}</a>`;

      return translatedText
        .replace('[TERMS_LINK]', termsLink);
    },

    policyUrl() {
      return {
        terms: `https://policy.getpica.com/${this.userLanguage}/termini-condizioni-vendita/`,
        privacy: `https://policy.getpica.com/${this.userLanguage}/privacy-policy-pica/`,
      }
    }
  },
  methods: {
    ...methods,
    ...mapActions('login', [
      'createUser',
      'getCurrentUser',
      'logout',
    ]),
    ...mapActions('userStatus', {fetchUserStatus: 'fetchStatus'}),

    canSkipDisambiguation() {
      const region = this.$route.query.region
      return region && this.hasRegion(region)
    },

    async confirmAccount() {
      if (!await this.v$.$validate()) return
      this.loading = true
      try {
        await this.afterLogin()
      } finally {
        this.loading = false
      }
    },

    async doLogout() {
      await this.logout()
    },

    async signup() {
      if (!await this.v$.$validate()) return
      this.submitted = true
      this.error = null
      if (this.isValid) {
        try {
          this.loading = true
          await this.createUser({
            email: this.email
          })

          // The action handles both cases and sets registrationFlow
          // Always redirect to login page which will handle the appropriate flow
          await this.$router.push({
            name: 'login',
            query: {
              email: this.email,
              flow: this.registrationFlow // Pass the flow type to login page
            }
          })
        } catch (e) {
          // Only show error if it wasn't handled by the action
          if (await this.manageLoginError(e, this.email)) return
          this.error = this.$gettext('An error occurred during registration. Please try again.')
        } finally {
          this.loading = false
        }
      }
    },
  }
}
</script>
